import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse, ITableFilters, ITableQuery } from '../../types';
import { parseQueryParams } from '../../utils';
import { ExportActions } from '../../enums';
import { baseQueryWithReauth } from './baseQuery';
import {
  SummaryResponse,
  MonthlyClaimsResponse,
  ApprovalFunnelResponse,
  AverageTimeResponse,
  IBulkApprovalActionDto,
  ITravelListQuery,
  TopVisitedCitiesResponse,
  TopExpenseTypesResponse,
  TravelHomeDashboard,
  ITravelOcrDraftResponseData,
} from '../../pages/TravelRequest/constants/travelreq.interface';
import {
  TourRequestType,
  TravelAttachmentReadSource,
  TravelRequestMarkAsPaidStatus,
} from '../../pages/TravelRequest/constants/travelreq.enum';
import {
  IAttachmentGalleryItem,
  ITravelRequestAttachmentsResponse,
} from '../../types/attachment';
import { PreApprovedToursSortByOptions } from '../../components/common/components/PreApprovedToursSearchHelp';
import { LogsSortByOptions } from '../../pages/TravelRequest/TravelReqDetail';
import { File } from 'buffer';

export interface TravelPortfolioCounts {
  all: number;
  needsAttention: number;
  drafts: number;
  pending: number;
  inReview: number;
  onHold: number;
  awaitingClaim: number;
  approved: number;
  rejected: number;
  cancelled: number;
  unclassified: number;
}

export interface TravelAnalyticsQuery {
  submittedOnFrom?: string;
  submittedOnTo?: string;
  companyGroupId?: number;
}

export interface ConveyanceBulkQuery {
  categoryId: number;
  subcategoryId: number;
  companyId?: number;
  companyGroupId?: number;
}

export interface ConveyanceBulkRow {
  source?: string;
  destination?: string;
  fromDate?: string;
  toDate?: string;
  distanceInKm?: number;
  total?: number;
  ratePerKm?: number;
  modeOfTransport?: number;
  modeOfTransportName?: string;
  remarks?: string;
}

const travelRequestApi = createApi({
  reducerPath: 'travelRequest',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'TravelRequest',
    'TravelDashboard',
    'TravelApprovals',
    'TravelReports',
    'HandsetLimitMapping',
    'TravelFormFieldMapping',
    'TravelApprovalMapping',
    'TravelLimits',
    'TravelHandsetMapping',
    'ConveyanceLimitMapping',
    'TravelCity',
    'TravelPreApprovedTours',
    'TravelFormFieldMappingForExpense',
    'TravelPrint',
    'ClaimAttachmentJson',
    'TravelApprovalLogs',
    'TravelChangeLogs',
    'TravelSapLogs',
    'ExchangeRate',
    'taxCode',
    'TravelAntiPatternMapping',
    'CustomerMaterialMapping',
    'cronjob',
  ],
  endpoints: builder => ({
    getTravelHomeDashboard: builder.query<
      BackendResponse<TravelHomeDashboard>,
      TravelAnalyticsQuery
    >({
      query: params => ({
        url: `/travel-analytics/home${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelDashboard'],
    }),
    getMonthlyClaims: builder.query<
      BackendResponse<MonthlyClaimsResponse>,
      TravelAnalyticsQuery | void
    >({
      query: params => ({
        url: `/travel-analytics/claims-monthly${params ? parseQueryParams(params) : ''}`,
        method: 'GET',
      }),
      providesTags: ['TravelDashboard'],
    }),
    getApprovalFunnel: builder.query<
      BackendResponse<ApprovalFunnelResponse>,
      TravelAnalyticsQuery | void
    >({
      query: params => ({
        url: `/travel-analytics/approval-funnel${params ? parseQueryParams(params) : ''}`,
        method: 'GET',
      }),
      providesTags: ['TravelDashboard'],
    }),
    // 🌍 Header Data
    getCountries: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/countries/all', method: 'GET' }),
    }),
    getStates: builder.query<
      BackendResponse<any>,
      { countryId?: number } | void
    >({
      query: query => ({
        url: `/master/states/all${query != null && typeof query === 'object' ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getCities: builder.query<
      BackendResponse<any>,
      { countryId?: number; stateId?: number }
    >({
      query: query => ({
        url: `/master/cities/all${parseQueryParams(query)}`,
        method: 'GET',
      }),
      providesTags: ['TravelCity'],
    }),
    getClaimCategories: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/claim-categories/all', method: 'GET' }),
    }),
    getClaimCategoriesForReq: builder.query<BackendResponse<any>, void>({
      query: () => ({
        url: '/master/claim-categories/for-request',
        method: 'GET',
      }),
    }),
    getClaimCategoriesForMapping: builder.query<
      BackendResponse<any>,
      Partial<{ companyId: number; companyGroupId: number }> | null
    >({
      query: query => ({
        url: `/master/claim-categories/for-mapping${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getClaimSubCategoriesForMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        companyId: number;
        companyGroupId: number;
        categoryId: number;
      }> | null
    >({
      query: query => ({
        url: `/master/claim-subcategories/for-mapping${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getClaimSubcategories: builder.query<
      BackendResponse<any>,
      Partial<{ categoryId: number }> | null
    >({
      query: query => ({
        url: `/master/claim-subcategories/all${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getClaimSubcategoriesForReq: builder.query<
      BackendResponse<any>,
      Partial<{ categoryId: number }> | null
    >({
      query: query => ({
        url: `/master/claim-subcategories/for-request${query ? parseQueryParams(query) : ''}`,
        method: 'GET',
      }),
    }),
    getResolveClaimType: builder.query<
      BackendResponse<{
        claimTypes: Array<{
          claimType: string | null;
          claimCategoryId: number;
          claimCategoryName: string;
          claimSubCategoryId: number;
          claimSubCategoryName: string;
        }>;
      }>,
      Partial<{ claimCategoryId: number; claimSubCategoryId: number }> | void
    >({
      query: params => ({
        url: `/travel/resolve-claim-type${params ? parseQueryParams(params) : ''}`,
        method: 'GET',
      }),
    }),
    getCostCenters: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/cost-centers/all', method: 'GET' }),
    }),
    getCurrencies: builder.query<BackendResponse<any>, { countryId?: number }>({
      query: query => ({
        url: `/master/currencies/all${parseQueryParams(query)}`,
        method: 'GET',
      }),
    }),
    // Mode of Journey
    getJourneyModes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/mode-of-journey/all', method: 'GET' }),
    }),

    // Accommodation Type
    getAccommodationTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/accommodation-types/all', method: 'GET' }),
    }),

    // Expense Claim
    getExpenseTypes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/expense-types/all', method: 'GET' }),
    }),

    getTravelExpenseTypesByClaim: builder.query<
      BackendResponse<any>,
      {
        claimCategory: number;
        claimSubCategory: number;
        grade: number;
        companyId?: number;
        companyGroupId?: number;
      }
    >({
      query: params => ({
        url: `/travel-mappings/expense-types${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getPaymentMethods: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/payment-methods/all', method: 'GET' }),
    }),
    getGstCodes: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/gst-codes/all', method: 'GET' }),
    }),
    getGlAccounts: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/gl-accounts/all', method: 'GET' }),
    }),
    getVendors: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/vendors/all', method: 'GET' }),
    }),

    getPerKmModeOfTransportDropdown: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/mode-of-transports/all', method: 'GET' }),
    }),

    getTransportModes: builder.query<
      BackendResponse<any>,
      Partial<{
        userId?: number;
        companyId?: number;
        companyGroupId?: number;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/perkm-mode-of-transport-dropdown${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getCustomers: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/customers/all', method: 'GET' }),
    }),
    getLanNo: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/lans/all', method: 'GET' }),
    }),
    getDsaCode: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/dsa/all', method: 'GET' }),
    }),

    getDesignations: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/designations/all', method: 'GET' }),
    }),
    getEmploymentType: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/employment-types/all', method: 'GET' }),
    }),
    getEmploymentGrades: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/employment-grades/all', method: 'GET' }),
    }),
    getEmployee: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/customers/all', method: 'GET' }),
    }),
    getDepartments: builder.query<BackendResponse<any>, void>({
      query: () => ({ url: '/master/departments/all', method: 'GET' }),
    }),

    // Travel Request CRUD
    createTravelRequest: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest', 'TravelDashboard', 'TravelApprovals'];
      },
    }),
    downloadConveyanceBulkTemplate: builder.query<Blob, ConveyanceBulkQuery>({
      query: params => ({
        url: `/travel/conveyance/bulk-template${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),
    parseConveyanceBulkUpload: builder.mutation<
      BackendResponse<{ rows: ConveyanceBulkRow[] }>,
      ConveyanceBulkQuery & { file: globalThis.File }
    >({
      query: ({ file, ...params }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/travel/conveyance/bulk-parse${parseQueryParams(params)}`,
          method: 'POST',
          body: formData,
        };
      },
    }),
    approveTravelRequest: builder.mutation<BackendResponse<any>, any>({
      query: ({ id, ...rest }) => ({
        url: `/travel-approvals/${id}`,
        method: 'POST',
        body: rest,
      }),
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelRequest',
              'TravelDashboard',
              { type: 'TravelRequest', id },
              { type: 'TravelApprovals' },
              { type: 'TravelReports' },
              { type: 'TravelPreApprovedTours' },
              { type: 'ClaimAttachmentJson', id },
              { type: 'TravelApprovalLogs', id },
              { type: 'TravelChangeLogs', id },
            ],
    }),
    submitTravelRequest: builder.mutation<
      BackendResponse<any>,
      { id: number; remarks?: string; submissionAttemptId?: string }
    >({
      query: ({ id, remarks, submissionAttemptId }) => ({
        url: `/travel/${id}/submit`,
        method: 'POST',
        body: { remarks, submissionAttemptId },
      }),
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelRequest',
              'TravelDashboard',
              { type: 'TravelRequest', id },
              { type: 'TravelApprovals' },
              { type: 'TravelReports' },
              { type: 'TravelPreApprovedTours' },
            ],
    }),
    uploadAttachments: builder.mutation<
      BackendResponse<any>,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/travel/${id}/attachments`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),
    updateAttachments: builder.mutation<
      BackendResponse<any>,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/travel/${id}/attachments`,
        method: 'PUT',
        body: formData,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),
    getAttachmentRecovery: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/travel/${id}/attachment-recovery`,
        method: 'GET',
      }),
      providesTags: (__, error, id) =>
        error ? [] : [{ type: 'TravelRequest', id }],
    }),
    parseDocument: builder.mutation<BackendResponse<any>, FormData>({
      query: formData => ({
        url: '/document/parse',
        method: 'POST',
        body: formData,
      }),
    }),
    createOcrDraft: builder.mutation<
      BackendResponse<ITravelOcrDraftResponseData>,
      FormData
    >({
      query: formData => ({
        url: '/travel/ocr-draft',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['TravelRequest', 'TravelDashboard'],
    }),
    updateTravelRequest: builder.mutation<BackendResponse<any>, any>({
      query: ({ id, ...rest }) => ({
        url: `/travel/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : [
              'TravelRequest',
              'TravelDashboard',
              'TravelApprovals',
              { type: 'TravelRequest', id: payload?.id },
              { type: 'ClaimAttachmentJson', id: payload?.id },
              { type: 'TravelChangeLogs', id: payload?.id },
            ],
    }),
    deleteTravelRequest: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/travel/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : [
              'TravelRequest',
              'TravelDashboard',
              { type: 'TravelRequest', id: payload?.id },
            ],
    }),

    // Update Travel Request Paid Status
    updateTravelRequestPaidStatus: builder.mutation<
      BackendResponse<any>,
      { requestIds: number[]; status: TravelRequestMarkAsPaidStatus }
    >({
      query: payload => ({
        url: '/travel/update-paid-status',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_, error, { requestIds = [] }) =>
        error
          ? []
          : [
              'TravelRequest' as const,
              ...(requestIds?.map(id => ({
                type: 'TravelRequest' as const,
                id: +id,
              })) || []),
            ],
    }),
    getTravelPortfolioCounts: builder.query<
      BackendResponse<TravelPortfolioCounts>,
      Partial<ITravelListQuery>
    >({
      query: params => ({
        url: `/travel/portfolio-counts${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),
    getTravelRequests: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),
    getTravelRequestById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/travel/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'TravelRequest', id }],
    }),

    // Travel Request Approvals
    getSubmittedTravelRequests: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-request/approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelApprovals'],
    }),

    //  Travel Request Reports
    getTravelRequestReport: builder.query<
      BackendResponse<{ list: any[]; count?: number }>,
      ITableQuery
    >({
      query: params => {
        return {
          url: `/travel-reports/${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: (__, err) => (err ? [] : ['TravelReports']),
    }),

    // Download Travel Request Report as Excel
    downloadTravelRequestReport: builder.query<
      Blob,
      { action: ExportActions; query: any }
    >({
      query: ({ action, query }) => ({
        url: `/travel-reports/export/${action}${parseQueryParams(query)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    // Mapping CURD

    // CalendarRestriction Mapping
    createCalendarRestrictionMapping: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => ({
        url: '/travel-mappings/calendar-restriction',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),
    getCalendarRestrictionMappings: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/calendar-restriction${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),
    updateCalendarRestrictionMapping: builder.mutation<
      BackendResponse<any>,
      any
    >({
      query: payload => ({
        url: `/travel-mappings/calendar-restriction/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),
    deleteCalendarRestrictionMapping: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: `/travel-mappings/calendar-restriction`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Perdiem Mapping
    createPerDiemMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/perdiem',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),
    getPerDiemMappings: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/perdiem${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),
    updatePerDiemMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/perdiem/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),
    deletePerDiemMapping: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: `/travel-mappings/perdiem`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // PerKm Mapping
    createPerKmMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/perkm',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),
    getPerKmMappings: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/perkm${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),
    updatePerKmMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/perkm/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),
    deletePerKmMapping: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: `/travel-mappings/perkm`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // ExpenseType & GlAccountMapping
    createExpenseType: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/expense-type-limit',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    getExpenseType: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/expense-type-limit${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateExpenseType: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/expense-type-limit/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),

    deleteExpenseType: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: `/travel-mappings/expense-type-limit`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // DesignationModeOfJourney
    createDesignationModeOfJourney: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: '/travel-mappings/designation-mode-of-journey',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: (_, error) => {
          if (error) {
            return [];
          }
          return ['TravelRequest'];
        },
      },
    ),

    getDesignationModeOfJourney: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/designation-mode-of-journey${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateDesignationModeOfJourney: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: `/travel-mappings/designation-mode-of-journey/${payload.id}`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: (_, error, payload) =>
          error
            ? []
            : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
      },
    ),

    deleteDesignationModeOfJourney: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: `/travel-mappings/designation-mode-of-journey`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Create Designation Expense Type
    createDesignationExpenseType: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/designation-expense-type',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    getDesignationExpenseTypes: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/designation-expense-type${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateDesignationExpenseType: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/designation-expense-type/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),

    deleteDesignationExpenseType: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: `/travel-mappings/designation-expense-type`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Expense Type Limit
    createExpenseTypeLimit: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/expense-type-limit',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    getExpenseTypeLimits: builder.query<
      BackendResponse<any>,
      {
        expenseType: number;
        grade: number;
        claimCategory?: number;
        claimSubCategory?: number;
      }
    >({
      query: params => ({
        url: `/travel-mappings/expense-type-limit${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateExpenseTypeLimit: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/expense-type-limit/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),

    deleteExpenseTypeLimit: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: `/travel-mappings/expense-type-limit`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Expense Anti Pattern
    getTravelAntiPatternMappings: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        companyGroupId: number;
        companyId: number;
        claimCategoryId: number;
        claimSubCategoryId: number;
        accommodationTypeId: number;
        modeOfJourneyId: number;
        modeOfTransportId: number;
        expenseTypeId: number;
        complementExpenseTypeId: number;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/anti-pattern${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelAntiPatternMapping'],
    }),
    getTravelAntiPatternMappingById: builder.query<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/travel-mappings/anti-pattern/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'TravelAntiPatternMapping', id }],
    }),
    createTravelAntiPatternMappings: builder.mutation<
      BackendResponse<any>,
      { rows: any[] }
    >({
      query: payload => ({
        url: '/travel-mappings/anti-pattern',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelAntiPatternMapping'];
      },
    }),
    updateTravelAntiPatternMappings: builder.mutation<
      BackendResponse<any>,
      { rows: any[] }
    >({
      query: payload => ({
        url: '/travel-mappings/anti-pattern',
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['TravelAntiPatternMapping'],
    }),
    deleteTravelAntiPatternMappings: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: '/travel-mappings/anti-pattern',
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) =>
        error ? [] : ['TravelAntiPatternMapping'],
    }),

    // Designation & Accommodation
    createDesignationAccommodation: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: '/travel-mappings/designation-accommodation',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: (_, error) => {
          if (error) {
            return [];
          }
          return ['TravelRequest'];
        },
      },
    ),

    getDesignationAccommodation: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/designation-accommodation${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateDesignationAccommodation: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: `/travel-mappings/designation-accommodation/${payload.id}`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: (_, error, payload) =>
          error
            ? []
            : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
      },
    ),

    deleteDesignationAccommodation: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: '/travel-mappings/designation-accommodation',
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Department & Cost Center
    createDepartmentCostCenter: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: 'travel-mappings/expense-type-gl',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    getDepartmentCostCenter: builder.query<BackendResponse<any>, ITableFilters>(
      {
        query: params => ({
          url: `travel-mappings/expense-type-gl${parseQueryParams(params)}`,
          method: 'GET',
        }),
        providesTags: ['TravelRequest'],
      },
    ),

    updateDepartmentCostCenter: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `travel-mappings/expense-type-gl/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),

    deleteDepartmentCostCenter: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: 'travel-mappings/expense-type-gl',
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    // Department & GL Account
    createDepartmentGLAccount: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/department-gl-account',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    getDepartmentGLAccount: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/department-gl-account${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateDepartmentGLAccount: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/department-gl-account/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
    }),

    deleteDepartmentGLAccount: builder.mutation<BackendResponse<any>, number[]>(
      {
        query: ids => ({
          url: '/travel-mappings/department-gl-account',
          method: 'DELETE',
          params: { ids: ids.join(',') },
        }),
        invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
      },
    ),

    // User Handset Mappings
    createUserHandsetMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/handset-limit',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelHandsetMapping'];
      },
    }),
    getUserHandsetMappings: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/handset-limit${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelHandsetMapping'],
    }),
    updateUserHandsetMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/handset-limit/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error ? [] : [{ type: 'TravelHandsetMapping', id: payload?.id }],
    }),
    deleteUserHandsetMapping: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: `/travel-mappings/handset-limit`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelHandsetMapping']),
    }),

    // Conveyance Limit Mappings
    createConveyanceLimitMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/conveyance-limit',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ConveyanceLimitMapping'];
      },
    }),

    getConveyanceLimitMappings: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/conveyance-limit${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ConveyanceLimitMapping'],
    }),

    updateConveyanceLimitMapping: builder.mutation<
      BackendResponse<any>,
      { payload: any; id: any }
    >({
      query: ({ payload, id }) => ({
        url: `/travel-mappings/conveyance-limit/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ConveyanceLimitMapping']),
    }),

    deleteConveyanceLimitMapping: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: `/travel-mappings/conveyance-limit`,
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ConveyanceLimitMapping']),
    }),

    // Claim Category & Sub Category
    createClaimCategorySubCategory: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: '/travel-mappings/claim-category-sub-category',
          method: 'POST',
          body: payload,
        }),
        invalidatesTags: (_, error) => {
          if (error) {
            return [];
          }
          return ['TravelRequest'];
        },
      },
    ),

    getClaimCategorySubCategory: builder.query<
      BackendResponse<any>,
      ITableFilters
    >({
      query: params => ({
        url: `/travel-mappings/claim-category-sub-category${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelRequest'],
    }),

    updateClaimCategorySubCategory: builder.mutation<BackendResponse<any>, any>(
      {
        query: payload => ({
          url: `/travel-mappings/claim-category-sub-category/${payload.id}`,
          method: 'PUT',
          body: payload,
        }),
        invalidatesTags: (_, error, payload) =>
          error
            ? []
            : ['TravelRequest', { type: 'TravelRequest', id: payload?.id }],
      },
    ),

    deleteClaimCategorySubCategory: builder.mutation<
      BackendResponse<any>,
      number[]
    >({
      query: ids => ({
        url: '/travel-mappings/claim-category-sub-category',
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelRequest']),
    }),

    createCityMapping: builder.mutation<BackendResponse<number>, any>({
      query: payload => ({
        url: '/master/city-mapping',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    createStateMapping: builder.mutation<BackendResponse<number>, any>({
      query: payload => ({
        url: '/master/state-mapping',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    createCountryMapping: builder.mutation<BackendResponse<number>, any>({
      query: payload => ({
        url: '/master/country-mapping',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelRequest'];
      },
    }),

    // Country-City Mapping Category
    createCountryCityMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/master/country-city-mapping',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelCity'];
      },
    }),

    // Bulk create (master) – use only these for Add Country / Add State / Add City flows
    createBulkCountries: builder.mutation<
      BackendResponse<any>,
      Array<{
        ShortDescription: string;
        LongDescription: string;
        Code?: string;
        Name?: string;
      }>
    >({
      query: body => ({
        url: '/master/countries',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelCity']),
    }),
    createBulkStates: builder.mutation<
      BackendResponse<any>,
      {
        countryId: number;
        states: Array<{
          ShortDescription: string;
          StateCode: string;
          LongDescription?: string;
        }>;
      }
    >({
      query: body => ({
        url: '/master/states',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelCity']),
    }),
    createBulkCities: builder.mutation<
      BackendResponse<any>,
      {
        countryId?: number;
        stateId?: number;
        cities: Array<{
          ShortDescription: string;
          LongDescription?: string;
          Code?: string;
        }>;
      }
    >({
      query: body => ({
        url: '/master/cities',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelCity']),
    }),

    getCountryCityMapping: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/master/cities${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelCity'],
    }),

    // useless api
    updateCountryCityMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/travel-mappings/country-city/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelCity']),
    }),

    deleteCountryCityMapping: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: '/master/country-city',
        method: 'DELETE',
        params: { ids: ids.join(',') },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['TravelCity']),
    }),

    getHandsetLimitMapping: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/travel-mappings/handset-limit${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['HandsetLimitMapping'],
    }),
    createHandsetLimitMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel-mappings/handset-limit',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['HandsetLimitMapping'];
      },
    }),
    deleteHandsetLimitMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-mappings/handset-limit${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['HandsetLimitMapping'];
      },
    }),
    createFormFieldMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-mappings/form-field`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelFormFieldMapping', 'TravelFormFieldMappingForExpense'];
      },
    }),
    updateFormFieldMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/travel-mappings/form-field/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'TravelFormFieldMapping',
          'TravelFormFieldMappingForExpense',
          { type: 'TravelFormFieldMapping', id: +id },
          { type: 'TravelFormFieldMappingForExpense', id: +id },
        ];
      },
    }),
    getFormFieldMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        categoryId: number;
        subcategoryId: number;
        requestType?: TourRequestType;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/form-field-categories${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelFormFieldMapping'],
    }),
    // NEW: Header-level form field mapping only
    getFormFieldMappingList: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        categoryId: number;
        subcategoryId: number;
        requestType?: TourRequestType;
        claimSubCategoryId?: number;
        tourRequestType?: TourRequestType;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/form-field${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelFormFieldMapping'],
    }),
    getFormFieldMappingForExpense: builder.query<
      BackendResponse<any>,
      Partial<{
        claimCategoryId: string;
        claimSubCategoryId: string;
        requestType?: TourRequestType;
        expenseType?: string;
        companyId?: number;
        companyGroupId?: number;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/form-field-expense${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelFormFieldMappingForExpense'],
    }),
    getFormFieldMappingById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/travel-mappings/form-field/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'TravelFormFieldMapping', id }],
    }),
    deleteFormFieldMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-mappings/form-field${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelFormFieldMapping'];
      },
    }),
    getTravelApprovalMapping: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        categoryId: number;
        subcategoryId: number;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/approval-config${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelApprovalMapping'],
    }),
    getTravelApprovalHierarchy: builder.query<
      Blob,
      Partial<{
        page: number;
        pageSize: number;
        categoryId: number;
        subcategoryId: number;
        userId: number;
      }>
    >({
      query: params => ({
        url: `/travel-mappings/approval-hierarchy${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: ['TravelApprovalMapping'],
    }),
    createTravelApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-mappings/approval-config`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelApprovalMapping'];
      },
    }),
    updateTravelApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        const { id, ...rest } = payload;
        return {
          url: `/travel-mappings/approval-config/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'TravelApprovalMapping',
          { type: 'TravelApprovalMapping', id: +id },
        ];
      },
    }),
    getTravelApprovalMappingById: builder.query<BackendResponse<any>, number>({
      query: id => ({
        url: `/travel-mappings/approval-config/${id}`,
        method: 'GET',
      }),
      providesTags: (__, err, id) =>
        err ? [] : [{ type: 'TravelApprovalMapping', id }],
    }),
    deleteTravelApprovalMapping: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-mappings/approval-config${parseQueryParams(payload)}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['TravelApprovalMapping'];
      },
    }),
    getTravelAttachment: builder.query<
      Blob,
      { travelRequestId: number; fileName: string }
    >({
      query: ({ travelRequestId, fileName }) => ({
        url: `/travel/${travelRequestId}/attachments/${encodeURIComponent(fileName)}`,
        responseHandler: async response => response.blob(),
      }),
    }),
    getTravelRequestAttachments: builder.query<
      BackendResponse<ITravelRequestAttachmentsResponse>,
      number
    >({
      query: id => ({
        url: `/travel/${id}/attachments`,
        method: 'GET',
      }),
      providesTags: (__, error, id) =>
        error ? [] : [{ type: 'TravelRequest', id }],
    }),
    getTravelRequestAttachmentFile: builder.query<
      Blob,
      {
        travelRequestId: number;
        source: TravelAttachmentReadSource;
        attachmentId: number;
      }
    >({
      query: ({ travelRequestId, source, attachmentId }) => ({
        url: `/travel/${travelRequestId}/attachments/${source}/${attachmentId}/file`,
        method: 'GET',
        responseHandler: async response => response.blob(),
      }),
    }),
    getTravelAttachmentMappings: builder.query<
      BackendResponse<any>,
      {
        id?: number;
        page?: number;
        pageSize?: number;
        fileName?: string;
        remarks?: string;
        sortBy?: string;
        fileId?: number;
      }
    >({
      query: ({ id, ...params }) => ({
        url: id
          ? `/travel/${id}/attachment-mappings${parseQueryParams(params)}`
          : `/travel/attachment-mappings${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    getAttachmentGallery: builder.query<
      BackendResponse<IAttachmentGalleryItem[]> | IAttachmentGalleryItem[],
      any
    >({
      query: params => ({
        url: `/attachment-gallery${params ? parseQueryParams(params) : ''}`,
        method: 'GET',
      }),
    }),
    uploadAttachmentGallery: builder.mutation<
      BackendResponse<IAttachmentGalleryItem[]>,
      FormData
    >({
      query: formData => ({
        url: '/attachment-gallery/store',
        method: 'POST',
        body: formData,
      }),
    }),
    getAttachmentGalleryFile: builder.query<Blob, number>({
      query: id => ({
        url: `/attachment-gallery/download/${id}`,
        method: 'GET',
        responseHandler: async response => response.blob(),
      }),
    }),
    getTravelApprovals: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        companyGroupId: number;
      }>
    >({
      query: params => ({
        url: `/travel-approvals${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelApprovals'],
    }),
    getTravelModeOfTransportLimit: builder.query<
      BackendResponse<any>,
      Partial<{
        grade: number;
        modeOfTransport: number;
        userId?: number;
        companyId?: number;
        companyGroupId?: number;
      }>
    >({
      query: params => ({
        url: `/travel-limit/mode-of-transport${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelLimits'],
    }),
    pushClaimToErpById: builder.mutation<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/travel/push/erp`,
          method: 'POST',
          body: {
            id: +id,
          },
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelReports',
              { type: 'TravelRequest', id },
              { type: 'TravelApprovals' },
              { type: 'TravelReports' },
              { type: 'TravelSapLogs', id },
            ],
    }),
    downloadClaimJSONById: builder.mutation<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/travel/download/json`,
          method: 'POST',
          body: {
            id: +id,
          },
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelReports',
              { type: 'TravelRequest', id },
              { type: 'TravelApprovals' },
              { type: 'TravelReports' },
            ],
    }),
    downloadClaimAttachmentJsonById: builder.query<
      BackendResponse<any[]>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/travel/download-attachment-json/${id}`,
          method: 'GET',
        };
      },
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'ClaimAttachmentJson', id }, 'ClaimAttachmentJson'],
    }),
    getTravelApprovalLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/travel-approvals/logs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'TravelApprovalLogs', id: +id }],
    }),
    getTravelChangeLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/travel-reports/changelogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'TravelChangeLogs', id: +id }],
    }),
    getTravelSapLogsById: builder.query<
      BackendResponse<any>,
      { id: string; pageSize: number; page: number; sortBy: LogsSortByOptions }
    >({
      query: ({ id, pageSize, page, sortBy }) => ({
        url: `/travel/saplogs/${id}`,
        method: 'GET',
        params: { page, pageSize, sortBy },
      }),
      providesTags: (__, err, { id }) =>
        err ? [] : [{ type: 'TravelSapLogs', id: +id }],
    }),
    getMobileApplicationLink: builder.query<
      BackendResponse<any>,
      { key: string }
    >({
      query: (params: { key: string }) => ({
        url: `/travel/presigned-url/mobile${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
    approveNewRequestFromEmail: builder.mutation<BackendResponse<any>, any>({
      query: payload => {
        return {
          url: `/travel-approvals/approve-from-mail`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelDashboard',
              'TravelReports',
              { type: 'TravelRequest', id },
              { type: 'TravelApprovals' },
              { type: 'TravelReports' },
              { type: 'ClaimAttachmentJson', id },
            ],
    }),
    getConeyanceEntry: builder.query<BackendResponse<any>, any>({
      query: (params: any) => ({
        url: `/travel/conveyance${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),

    getSummary: builder.query<BackendResponse<SummaryResponse>, void>({
      query: () => '/travel-analytics/summary',
    }),
    getAverageApprovalTime: builder.query<
      BackendResponse<AverageTimeResponse>,
      void
    >({
      query: () => '/travel-analytics/average-approval-time',
    }),
    getPendingApprovalRequests: builder.query<
      BackendResponse<{ count: number }>,
      void
    >({
      query: () => '/travel-analytics/pending-approval-requests',
    }),
    getTopVisitedCities: builder.query<
      BackendResponse<TopVisitedCitiesResponse>,
      {
        limit?: number;
        submittedOnFrom?: string;
        submittedOnTo?: string;
        companyGroupId?: number;
      } | void
    >({
      query: params => ({
        url: '/travel-analytics/top-visited-cities',
        params: params || {},
      }),
      providesTags: ['TravelDashboard'],
    }),
    getTopExpenseTypes: builder.query<
      BackendResponse<TopExpenseTypesResponse>,
      {
        limit?: number;
        submittedOnFrom?: string;
        submittedOnTo?: string;
        companyGroupId?: number;
      } | void
    >({
      query: params => ({
        url: '/travel-analytics/top-expense-types',
        params: params || {},
      }),
      providesTags: ['TravelDashboard'],
    }),
    bulkApproveTravelRequests: builder.mutation<
      BackendResponse<void>,
      IBulkApprovalActionDto
    >({
      query: payload => ({
        url: '/travel-approvals/bulk-approval-action',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        'TravelDashboard',
        'TravelApprovals',
        'ClaimAttachmentJson',
      ],
    }),
    getPreApprovedTourRequests: builder.query<
      BackendResponse<any>,
      Partial<{
        page: number;
        pageSize: number;
        search?: string;
        sortBy?: PreApprovedToursSortByOptions;
      }>
    >({
      query: params => ({
        url: `/travel/pre-approved-tours${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelPreApprovedTours'],
    }),
    sendTravelRequestForClaim: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/travel/send-request-for-claim',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'TravelDashboard',
          'TravelPreApprovedTours',
          'TravelRequest',
          'TravelApprovals',
          'TravelReports',
          { type: 'TravelRequest', id },
          { type: 'TravelApprovalLogs', id },
          { type: 'TravelChangeLogs', id },
        ];
      },
    }),
    cancelClaimRequest: builder.mutation<
      BackendResponse<any>,
      { id: number; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/travel/cancel-request/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          'TravelDashboard',
          'TravelPreApprovedTours',
          'TravelRequest',
          'TravelApprovals',
          'TravelReports',
          { type: 'TravelRequest', id },
          { type: 'TravelApprovalLogs', id },
          { type: 'TravelChangeLogs', id },
        ];
      },
    }),
    getTravelLimitExpenseType: builder.query<
      BackendResponse<any>,
      {
        grade: number;
        expenseType: number;
        claimCategory?: number;
        claimSubCategory?: number;
        companyGroupId?: number;
        companyId?: number;
        userId?: number;
      }
    >({
      query: params => ({
        url: `/travel-limit/expense-type${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TravelLimits'],
    }),
    downloadTravelAttachmentsZip: builder.query<
      Blob,
      { travelRequestId: number }
    >({
      query: ({ travelRequestId }) => ({
        url: `/travel/${travelRequestId}/attachments/download-zip`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    printTravelRequest: builder.query<Blob, { id: number }>({
      query: ({ id }) => ({
        url: `/travel/print/${id}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
      providesTags: (_, __, { id }) => [{ type: 'TravelPrint', id }],
    }),

    resetApprovalHierarchy: builder.mutation<
      BackendResponse<void>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/travel-approvals/reset-approval-hierarchy/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelDashboard',
              'TravelApprovals',
              { type: 'TravelRequest' as const, id: +id },
              { type: 'TravelApprovalLogs' as const, id: +id },
              { type: 'TravelChangeLogs' as const, id: +id },
            ],
    }),

    rearrangeApprovalHierarchy: builder.mutation<
      BackendResponse<void>,
      {
        id: string;
        mappings: Array<{ approverId: number }>;
      }
    >({
      query: ({ id, mappings }) => ({
        url: `/travel-approvals/rearrange-approval-hierarchy/${id}`,
        method: 'POST',
        body: mappings,
      }),
      invalidatesTags: (_, error, { id }) =>
        error
          ? []
          : [
              'TravelDashboard',
              'TravelApprovals',
              { type: 'TravelRequest' as const, id: +id },
              { type: 'TravelApprovalLogs' as const, id: +id },
              { type: 'TravelChangeLogs' as const, id: +id },
            ],
    }),

    uploadTravelSAPExcel: builder.mutation<BackendResponse<any>, File>({
      query: file => {
        const formData = new FormData();
        formData.append('file', file as Blob); // must match FileInterceptor('file')

        return {
          url: '/travel-reports/sap/upload-excel',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_: any, error: any) => {
        if (error) return [];
        return ['TravelRequest', 'TravelReports'];
      },
    }),
    // Download Travel SAP data to be filled in excel template
    downloadTravelSAPExcel: builder.query<Blob, any>({
      query: () => ({
        url: `/travel-reports/sap/download-excel`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    createExchangeRate: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/master/exchange-rate',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ExchangeRate']),
    }),

    getExchangeRates: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/master/exchange-rate${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ExchangeRate'],
    }),

    updateExchangeRate: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/master/exchange-rate/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error
          ? []
          : ['ExchangeRate', { type: 'ExchangeRate', id: payload?.id }],
    }),

    deleteExchangeRates: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: '/master/exchange-rate/bulk',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['ExchangeRate']),
    }),

    downloadSampleExchangeRate: builder.query<Blob, void>({
      query: () => ({
        url: '/master/exchange-rate/download-sample',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    uploadExchangeRate: builder.mutation<BackendResponse<any>, FormData>({
      query: formData => ({
        url: '/master/exchange-rate/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['ExchangeRate'],
    }),

    getExchangeRateSyncLogs: builder.query<BackendResponse<any>, ITableFilters>(
      {
        query: params => ({
          url: `/master/exchange-rate/logs${parseQueryParams(params)}`,
          method: 'GET',
        }),
      },
    ),

    createTaxCode: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: '/master/gst-codes',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['taxCode']),
    }),

    getTaxCodes: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/master/mst-gst${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['taxCode'],
    }),

    updateTaxCode: builder.mutation<BackendResponse<any>, any>({
      query: payload => ({
        url: `/master/mst-gst/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) =>
        error ? [] : ['taxCode', { type: 'taxCode', id: payload?.id }],
    }),

    deleteTaxCodes: builder.mutation<BackendResponse<any>, number[]>({
      query: ids => ({
        url: '/master/mst-gst/bulk',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (_, error) => (error ? [] : ['taxCode']),
    }),

    downloadSampleTaxCode: builder.query<Blob, void>({
      query: () => ({
        url: '/master/mst-gst/download-sample',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    uploadTaxCode: builder.mutation<BackendResponse<any>, FormData>({
      query: formData => ({
        url: '/master/mst-gst/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['taxCode'],
    }),

    createCustomerMaterialMapping: builder.mutation<
      BackendResponse<any>,
      { customerId: number; materialIds: number[] }
    >({
      query: payload => ({
        url: '/business-mapping/customer-material-mapping',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['CustomerMaterialMapping']),
    }),

    getCustomerMaterialMappings: builder.query<
      BackendResponse<any>,
      ITableFilters & {
        customerId?: number | null;
        materialId?: number | null;
        customer?: string;
        material?: string;
      }
    >({
      query: params => ({
        url: `/business-mapping/customer-material-mapping${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['CustomerMaterialMapping'],
    }),

    updateCustomerMaterialMapping: builder.mutation<
      BackendResponse<any>,
      { id: number; customerId: number; materialId: number; status: number }
    >({
      query: ({ id, ...payload }) => ({
        url: `/business-mapping/customer-material-mapping/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['CustomerMaterialMapping']),
    }),

    deleteCustomerMaterialMapping: builder.mutation<
      BackendResponse<any>,
      number
    >({
      query: id => ({
        url: `/business-mapping/customer-material-mapping/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (error ? [] : ['CustomerMaterialMapping']),
    }),

    bulkDeleteCustomerMaterialMapping: builder.mutation<
      BackendResponse<any>,
      { customerId: number; materialIds: number[] }
    >({
      query: body => ({
        url: '/business-mapping/customer-material-mapping/bulk-delete',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['CustomerMaterialMapping']),
    }),

    exportCustomerMaterialMapping: builder.query<Blob, any>({
      query: params => ({
        url: `/business-mapping/customer-material-mapping/export${parseQueryParams(params)}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    downloadSampleCustomerMaterialMapping: builder.query<Blob, void>({
      query: () => ({
        url: '/business-mapping/customer-material-mapping/sample-download',
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    uploadCustomerMaterialMapping: builder.mutation<
      BackendResponse<any>,
      FormData
    >({
      query: formData => ({
        url: '/business-mapping/customer-material-mapping/bulk-upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['CustomerMaterialMapping'],
    }),

    triggerCronJob: builder.mutation<BackendResponse<any>, { type: string }>({
      query: type => ({
        url: `/sales-order/sap-requests/cron-job`,
        method: 'POST',
        body: type,
      }),
      invalidatesTags: (_, error) => (error ? [] : ['cronjob']),
    }),

    getCronJobLogs: builder.query<BackendResponse<any>, ITableFilters>({
      query: params => ({
        url: `/sales-order/sap-requests/cron-job${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['cronjob'],
    }),

    checkTravelCreateAccess: builder.query<
      BackendResponse<{ createButton: boolean }>,
      { module: string }
    >({
      query: params => ({
        url: `/users/check-create-access${parseQueryParams(params)}`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetTravelHomeDashboardQuery,
  useCancelClaimRequestMutation,
  useSendTravelRequestForClaimMutation,
  useGetPreApprovedTourRequestsQuery,
  useGetTravelPortfolioCountsQuery,
  useGetCountriesQuery,
  useGetCitiesQuery,
  useGetClaimCategoriesQuery,
  useGetClaimSubcategoriesQuery,
  useGetCostCentersQuery,
  useGetDepartmentsQuery,
  useGetCurrenciesQuery,
  useGetJourneyModesQuery,
  useGetAccommodationTypesQuery,
  useGetExpenseTypesQuery,
  useGetTravelExpenseTypesByClaimQuery,
  useGetPaymentMethodsQuery,
  useGetTransportModesQuery,
  useCreateTravelRequestMutation,
  useLazyDownloadConveyanceBulkTemplateQuery,
  useParseConveyanceBulkUploadMutation,
  useUpdateTravelRequestMutation,
  useDeleteTravelRequestMutation,
  useGetTravelRequestsQuery,
  useGetTravelRequestByIdQuery,
  useGetConeyanceEntryQuery,
  useUpdateTravelRequestPaidStatusMutation,
  useGetPerKmModeOfTransportDropdownQuery,

  // Mapping Start

  // calendarRestriction
  useCreateCalendarRestrictionMappingMutation,
  useGetCalendarRestrictionMappingsQuery,
  useUpdateCalendarRestrictionMappingMutation,
  useDeleteCalendarRestrictionMappingMutation,

  // Perdiem
  useCreatePerDiemMappingMutation,
  useGetPerDiemMappingsQuery,
  useUpdatePerDiemMappingMutation,
  useDeletePerDiemMappingMutation,

  // Perkm
  useCreatePerKmMappingMutation,
  useGetPerKmMappingsQuery,
  useUpdatePerKmMappingMutation,
  useDeletePerKmMappingMutation,

  // ExpenseType & GlAcoount
  useCreateExpenseTypeMutation,
  useGetExpenseTypeQuery,
  useUpdateExpenseTypeMutation,
  useDeleteExpenseTypeMutation,

  //Designation & Mode of Journey
  useCreateDesignationModeOfJourneyMutation,
  useGetDesignationModeOfJourneyQuery,
  useUpdateDesignationModeOfJourneyMutation,
  useDeleteDesignationModeOfJourneyMutation,

  // Designation & Expense Type
  useCreateDesignationExpenseTypeMutation,
  useGetDesignationExpenseTypesQuery,
  useUpdateDesignationExpenseTypeMutation,
  useDeleteDesignationExpenseTypeMutation,

  // Expense Type Limit
  useCreateExpenseTypeLimitMutation,
  useGetExpenseTypeLimitsQuery,
  useLazyGetExpenseTypeLimitsQuery,
  useUpdateExpenseTypeLimitMutation,
  useDeleteExpenseTypeLimitMutation,
  useGetTravelAntiPatternMappingsQuery,
  useGetTravelAntiPatternMappingByIdQuery,
  useLazyGetTravelAntiPatternMappingByIdQuery,
  useCreateTravelAntiPatternMappingsMutation,
  useUpdateTravelAntiPatternMappingsMutation,
  useDeleteTravelAntiPatternMappingsMutation,

  // Designation & Accommodation
  useCreateDesignationAccommodationMutation,
  useGetDesignationAccommodationQuery,
  useUpdateDesignationAccommodationMutation,
  useDeleteDesignationAccommodationMutation,

  // Department Cost Center
  useCreateDepartmentCostCenterMutation,
  useGetDepartmentCostCenterQuery,
  useUpdateDepartmentCostCenterMutation,
  useDeleteDepartmentCostCenterMutation,

  // Department GL Account
  useCreateDepartmentGLAccountMutation,
  useGetDepartmentGLAccountQuery,
  useUpdateDepartmentGLAccountMutation,
  useDeleteDepartmentGLAccountMutation,

  // Handset
  useCreateUserHandsetMappingMutation,
  useGetUserHandsetMappingsQuery,
  useUpdateUserHandsetMappingMutation,
  useDeleteUserHandsetMappingMutation,

  // ConveyanceLimit
  useCreateConveyanceLimitMappingMutation,
  useGetConveyanceLimitMappingsQuery,
  useUpdateConveyanceLimitMappingMutation,
  useDeleteConveyanceLimitMappingMutation,

  // Claim Category Sub Category
  useCreateClaimCategorySubCategoryMutation,
  useGetClaimCategorySubCategoryQuery,
  useUpdateClaimCategorySubCategoryMutation,
  useDeleteClaimCategorySubCategoryMutation,

  // country & clity
  useCreateCityMappingMutation,
  useCreateStateMappingMutation,
  useCreateCountryMappingMutation,
  useCreateCountryCityMappingMutation,
  useCreateBulkCountriesMutation,
  useCreateBulkStatesMutation,
  useCreateBulkCitiesMutation,
  useGetCountryCityMappingQuery,
  useUpdateCountryCityMappingMutation,
  useDeleteCountryCityMappingMutation,

  // Exchange Rate
  useCreateExchangeRateMutation,
  useGetExchangeRatesQuery,
  useUpdateExchangeRateMutation,
  useDeleteExchangeRatesMutation,
  useLazyDownloadSampleExchangeRateQuery,
  useUploadExchangeRateMutation,
  useGetExchangeRateSyncLogsQuery,

  // Tax Code
  useCreateTaxCodeMutation,
  useGetTaxCodesQuery,
  useUpdateTaxCodeMutation,
  useDeleteTaxCodesMutation,
  useLazyDownloadSampleTaxCodeQuery,
  useUploadTaxCodeMutation,

  // Customer Material Mapping
  useCreateCustomerMaterialMappingMutation,
  useGetCustomerMaterialMappingsQuery,
  useUpdateCustomerMaterialMappingMutation,
  useDeleteCustomerMaterialMappingMutation,
  useBulkDeleteCustomerMaterialMappingMutation,
  useLazyExportCustomerMaterialMappingQuery,
  useLazyDownloadSampleCustomerMaterialMappingQuery,
  useUploadCustomerMaterialMappingMutation,

  // cron job
  useGetCronJobLogsQuery,
  useTriggerCronJobMutation,

  useGetSubmittedTravelRequestsQuery,
  useGetTravelRequestReportQuery,
  useLazyDownloadTravelRequestReportQuery,
  useLazyDownloadTravelSAPExcelQuery,
  useUploadTravelSAPExcelMutation,
  useGetStatesQuery,
  useGetGstCodesQuery,
  useGetGlAccountsQuery,
  useGetVendorsQuery,
  useGetCustomersQuery,
  useGetDsaCodeQuery,
  useGetLanNoQuery,
  useGetDesignationsQuery,
  useGetEmploymentTypeQuery,
  useGetEmploymentGradesQuery,
  useGetEmployeeQuery,
  useGetHandsetLimitMappingQuery,
  useCreateHandsetLimitMappingMutation,
  useDeleteHandsetLimitMappingMutation,
  useGetClaimCategoriesForReqQuery,
  useLazyGetClaimSubcategoriesForReqQuery,
  useLazyGetClaimSubcategoriesQuery,
  useGetResolveClaimTypeQuery,
  useLazyGetResolveClaimTypeQuery,
  useCreateFormFieldMappingMutation,
  useGetFormFieldMappingQuery,
  useGetFormFieldMappingByIdQuery,
  useDeleteFormFieldMappingMutation,
  useUpdateFormFieldMappingMutation,
  useLazyGetFormFieldMappingQuery,
  useGetFormFieldMappingListQuery,

  // travel approval mapping
  useGetTravelApprovalMappingQuery,
  useLazyGetTravelApprovalHierarchyQuery,
  useCreateTravelApprovalMappingMutation,
  useUpdateTravelApprovalMappingMutation,
  useGetTravelApprovalMappingByIdQuery,
  useDeleteTravelApprovalMappingMutation,
  useUploadAttachmentsMutation,
  useGetTravelAttachmentQuery,
  useLazyGetTravelAttachmentQuery,
  useLazyGetTravelRequestAttachmentsQuery,
  useLazyGetTravelRequestAttachmentFileQuery,
  useLazyGetTravelAttachmentMappingsQuery,
  useGetAttachmentGalleryQuery,
  useUploadAttachmentGalleryMutation,
  useLazyGetAttachmentGalleryFileQuery,
  useParseDocumentMutation,
  useCreateOcrDraftMutation,
  useApproveTravelRequestMutation,
  useSubmitTravelRequestMutation,
  useGetTravelApprovalsQuery,
  useLazyGetTravelModeOfTransportLimitQuery,
  usePushClaimToErpByIdMutation,
  useDownloadClaimJSONByIdMutation,
  useGetTravelApprovalLogsByIdQuery,
  useLazyGetMobileApplicationLinkQuery,
  useUpdateAttachmentsMutation,
  useGetAttachmentRecoveryQuery,
  useLazyGetAttachmentRecoveryQuery,
  useApproveNewRequestFromEmailMutation,
  useGetTravelChangeLogsByIdQuery,
  useGetTravelSapLogsByIdQuery,
  useLazyGetClaimCategoriesForMappingQuery,
  useLazyGetClaimSubCategoriesForMappingQuery,

  // for analytics
  useGetSummaryQuery,
  useGetMonthlyClaimsQuery,
  useGetApprovalFunnelQuery,
  useGetAverageApprovalTimeQuery,
  useGetPendingApprovalRequestsQuery,
  useGetTopVisitedCitiesQuery,
  useGetTopExpenseTypesQuery,
  useBulkApproveTravelRequestsMutation,
  useLazyGetFormFieldMappingForExpenseQuery,
  useLazyGetTravelLimitExpenseTypeQuery,
  useDownloadTravelAttachmentsZipQuery,
  useLazyDownloadTravelAttachmentsZipQuery,
  useLazyPrintTravelRequestQuery,
  useLazyDownloadClaimAttachmentJsonByIdQuery,
  useResetApprovalHierarchyMutation,
  useRearrangeApprovalHierarchyMutation,
  useCheckTravelCreateAccessQuery,
} = travelRequestApi;

export default travelRequestApi;
